import { useMemo, FC } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { getDate, updateImageUrl } from 'utils/convertString';
import { IBrandsCatalogue } from 'stores/brands/types';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { AvatarIcon } from 'components/Core/AvatarIcon/AvatarIcon';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { Switch } from 'components/Core/Switch/Switch';
import { BrandsCataloguesFiltersForm } from 'components/Forms/BrandsCataloguesFiltersForm/BrandsCataloguesFiltersForm';
import { BrandsCataloguesFiltersFormTransFields } from 'components/Forms/BrandsCataloguesFiltersForm/types';
import { brandsCataloguesFiltersFormTranslations } from 'components/Forms/BrandsCataloguesFiltersForm/translations';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { brandsCataloguesTableTranslations } from './translations';
import { BrandsCataloguesTableTransFields } from './types';
import styles from './brandsCataloguesTable.module.scss';

export const POINTS_TABLE_LIMIT = 100;
const AVATAR_SIZE = 56;

export const BrandsCataloguesTable: FC = observer(() => {
  const {
    brandsStore: { filteredBrandCatalogues, changeBrandCatalogueDublicatedStatus },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<
    | BrandsCataloguesTableTransFields
    | Extract<BrandsCataloguesFiltersFormTransFields, 'dublicatedStatus' | 'dublicated' | 'notDublicated'>
  >([brandsCataloguesTableTranslations.name, brandsCataloguesFiltersFormTranslations.name]);

  const { mutate, isLoading } = useMutation(changeBrandCatalogueDublicatedStatus, {
    mutationKey: 'changeBrandCatalogueDublicatedStatus',
    onSuccess: () => {
      showAlert(t('changeDuplicatedStatusSuccess'), 'success');
    },
  });

  const columns: Column<IBrandsCatalogue>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original,
            original: { previewUrl, name, isDuplicated },
          },
        }: CellProps<IBrandsCatalogue>) => (
          <div className={styles.nameCell}>
            <Switch
              isActive={!isDuplicated}
              onChange={(status) => mutate({ id: original.id, isDuplicated: !status })}
              disabled={isLoading}
              classNameProp={styles.isDuplicatedStatus}
            />
            <div className={styles.avatar}>
              <AvatarIcon
                avatar={updateImageUrl(previewUrl, AVATAR_SIZE * 2)}
                name='point image'
                size={AVATAR_SIZE}
                styleProps={{ borderRadius: 8 }}
              />
              {name ? <p className={styles.name}>{name}</p> : '—'}
            </div>
          </div>
        ),
        accessor: (row) => row.name,
        width: 9,
        // sortType: 'apiSort',
      },
      {
        Header: t('dublicatedStatus', { ns: brandsCataloguesFiltersFormTranslations.name }),
        id: 'isDuplicated',
        accessor: (row) =>
          t(row.isDuplicated ? 'dublicated' : 'notDublicated', { ns: brandsCataloguesFiltersFormTranslations.name }),
        width: 4,
        // sortType: 'apiSort',
      },
      {
        Header: t('createdAt'),
        id: 'createdAt',
        accessor: (row) => (row.createdAt ? getDate(row.createdAt) : '-'),
        width: 6,
        // sortType: 'apiSort',
      },
      {
        Header: t('startDate'),
        id: 'startDate',
        accessor: (row) => (row.startDate ? getDate(row.startDate) : '-'),
        width: 6,
        // sortType: 'apiSort',
      },
      {
        Header: t('expiredDate'),
        id: 'expiredDate',
        accessor: (row) => (row.expiredDate ? getDate(row.expiredDate) : '-'),
        width: 6,
        // sortType: 'apiSort',
      },
      {
        Header: t('isActive'),
        id: 'isActive',
        Cell: ({
          row: {
            original: { isActive },
          },
        }: CellProps<IBrandsCatalogue>) => (
          <div className={styles.statusRow}>
            <div className={`${styles.statusDot} ${!isActive && styles.inactive}`} />
            <p>{t(isActive ? 'active' : 'inactive')}</p>
          </div>
        ),
        accessor: (row) => row.isActive,
        width: 2,
        // sortType: 'apiSort',
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({
          row: {
            original: { id },
          },
        }: CellProps<IBrandsCatalogue>) => (
          <div className={styles.actionBoxBtn}>
            <Link to={`${id}`} className={styles.editBtn}>
              <EditIcon />
            </Link>
          </div>
        ),
        width: 2,
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line
    [isLoading]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      <BrandsCataloguesFiltersForm />
      {filteredBrandCatalogues && filteredBrandCatalogues.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...filteredBrandCatalogues]}
          simpleList
          isPaginated={filteredBrandCatalogues.length > PAGE_SIZE}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
});
